// ────────────────────────────────────────────────────────────
// STICKY STRIP — keeps the answer on screen while auditing links
// ────────────────────────────────────────────────────────────
// A slim fixed bar that appears once the page header scrolls out
// of view. Uses a plain scroll listener on the .dd-root scroll
// container (IntersectionObserver rides the rendering pipeline and
// proved unreliable to verify in headless panes; a scroll check is
// deterministic and costs one comparison per event).

import { useEffect, useRef, useState } from 'react';

const TOPBAR_ALLOWANCE = 48;   // sticky topbar height + a little slack

export function StickyStrip({ children }: { children: React.ReactNode }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    const root = el?.closest('.dd-root');
    if (!el || !(root instanceof HTMLElement)) return;

    const check = () => {
      const rootTop = root.getBoundingClientRect().top;
      const sentinelTop = el.getBoundingClientRect().top;
      setStuck(sentinelTop < rootTop + TOPBAR_ALLOWANCE);
    };
    check();
    root.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      root.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
      {stuck && <div className="dd-sticky-strip">{children}</div>}
    </>
  );
}
